<template>
  <v-container class="notification-settings-container">
    <!-- Notification Settings Card -->
    <v-card class="mb-6 settings-card" elevation="2">
      <v-card-title class="primary-header">
        <v-icon class="ml-2" dark>mdi-bell-ring</v-icon>
        הגדרות התראות וצלצול
      </v-card-title>

      <v-card-text>
        <!-- Ring Time -->
        <div class="setting-section">
          <div class="setting-header">
            <v-icon color="#0d2c6d" class="ml-2">mdi-timer</v-icon>
            <span class="setting-title">זמן צלצול בשניות:</span>
          </div>
          <v-select background-color="white" :items="ring_times" v-model="details.ring_time" outlined dense
            hide-details="auto" class="mt-2 compact-select"></v-select>
        </div>

        <!-- Outgoing Call ID -->
        <div class="setting-section alternate">
          <div class="setting-header">
            <v-icon color="#0d2c6d" class="ml-2">mdi-phone-outgoing</v-icon>
            <span class="setting-title">מספר זיהוי בשיחה יוצאת:</span>
          </div>
          <v-select background-color="white" :items="details.other_lines" v-model="details.out_calls_id" outlined dense
            hide-details="auto" class="mt-2 medium-select"></v-select>
        </div>

        <!-- Mute Including Private Messages -->
        <div class="setting-section">
          <div class="setting-header">
            <v-icon color="#0d2c6d" class="ml-2">mdi-bell-off</v-icon>
            <span class="setting-title">השתק כולל הודעות פרטיות:</span>
          </div>
          <v-radio-group v-model="groupValue" row class="mt-2">
            <v-radio v-for="option in groupType" :key="option" :label="option" :value="option" color="#0d2c6d"
              hide-details class="ml-3"></v-radio>
          </v-radio-group>
        </div>

        <!-- Mute Until Date -->
        <div class="setting-section alternate">
          <div class="setting-header">
            <v-icon color="#0d2c6d" class="ml-2">mdi-calendar-clock</v-icon>
            <span class="setting-title">השתק התראות עד לתאריך:</span>
          </div>
          <v-text-field background-color="white" v-model="details.system_mute.mute_until" outlined dense
            hide-details="auto" type="datetime-local" class="mt-2 medium-select"></v-text-field>
        </div>
      </v-card-text>
    </v-card>

    <!-- Schedule Mute Card -->
    <v-card class="mb-6 settings-card" elevation="2">
      <v-card-title class="secondary-header">
        <v-icon class="ml-2">mdi-clock-time-four</v-icon>
        הגדר זמן קבוע להשתקת צינתוק
      </v-card-title>

      <v-card-text>
        <v-simple-table class="schedule-table">
          <template v-slot:default>
            <thead>
              <tr class="table-header">
                <th class="text-center">יום</th>
                <th class="text-center">מצב</th>
                <th class="text-center">בין השעות</th>
                <th class="text-center">פעולות</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="([dayName, dayArray]) in Object.entries(details.system_mute.every_day_mute)">
                <tr v-for="(dayObjects, dayObjIndex) in dayArray" :key="`${dayName}-${dayObjIndex}`"
                  :class="{ 'alternate-row': dayObjIndex % 2 === 0 }">
                  <td class="text-center">
                    <span class="day-name">{{ dayObjects.hebrewDay }}</span>
                  </td>
                  <td class="text-center">
                    <v-switch color="success" v-model="dayObjects.status" inset hide-details
                      class="status-switch"></v-switch>
                  </td>
                  <td class="time-range-cell">
                    <div class="time-range-container">
                      <v-text-field background-color="white" v-model="dayObjects.startTime" hide-details outlined dense
                        type="time" class="time-input" :disabled="!dayObjects.status"></v-text-field>

                      <span class="mx-2">עד</span>

                      <v-text-field background-color="white" v-model="dayObjects.endTime" outlined dense hide-details
                        type="time" class="time-input" :disabled="!dayObjects.status"></v-text-field>
                    </div>
                  </td>
                  <td class="text-center action-cell">
                    <v-tooltip bottom v-if="dayObjIndex === 0 && dayArray.length < 3">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="primary" v-bind="attrs" v-on="on"
                          @click="addDateMute(dayName, dayObjects.hebrewDay, dayArray.length)">
                          <v-icon>mdi-plus-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>הוסף זמנים</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="dayObjIndex !== 0">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="error" v-bind="attrs" v-on="on"
                          @click="deleteDateMute(dayName, dayObjIndex)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>מחק זמנים</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->
    <div class="d-flex justify-end mb-8">
      <v-btn color="primary" x-large :loading="progressShow" :disabled="progressShow" @click="saveChanges"
        class="save-button">
        <v-icon left>mdi-content-save</v-icon>
        שמור שינויים
      </v-btn>
    </div>

    <!-- Loading Indicator -->
    <v-overlay :value="progressShow" absolute>
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Notification -->
    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </v-container>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import Util from '@/util/utils.js'

export default {
  components: {
    SnackBar,
  },
  data: () => ({
    snackbar: false,
    snackbarColorBt: "success",
    snacbarText: "",
    lineNumber: "",
    progressShow: false,
    details: {
      system_mute: {
        every_day_mute: {}
      },
      other_lines: []
    },
    days: [],
    groupType: ["כן", "לא"],
    groupValue: "",
    ring_times: [5, 6, 7, 8, 9, 10, 15, 20, 25],
  }),
  methods: {
    async getSettingsDetails() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/settings/get_system_mute_details`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "error");
        } else if (res.status === 200) {
          this.details = jsonObject;
          this.formatSettings();
        }
      } catch (error) {
        this.showSnackBar("Error get files: " + error, "error");
      } finally {
        this.progressShow = false;
      }
    },

    formatSettings() {
      if (this.details.system_mute.mute_include_privates) {
        this.groupValue = "כן";
      } else {
        this.groupValue = "לא";
      }

      if (this.details.system_mute.mute_until) {
        this.details.system_mute.mute_until = Util.getFormatDate(this.details.system_mute.mute_until);
      }

      if (this.details.other_lines && !this.details.other_lines.includes("default")) {
        this.details.other_lines.unshift("default");
      }
    },

    addDateMute(dayName, hebrewDay, dayArrayLength) {
      if (dayArrayLength < 3) {
        this.details.system_mute.every_day_mute[dayName].push({
          startTime: "",
          endTime: "",
          status: false,
          hebrewDay: hebrewDay
        });
      }
    },

    deleteDateMute(dayName, dayObjectIndex) {
      this.details.system_mute.every_day_mute[dayName].splice(dayObjectIndex, 1);
    },

    async saveChanges() {
      try {
        // Validate time ranges
        const everyDaysDetails = this.details.system_mute.every_day_mute;
        for (const key in everyDaysDetails) {
          if (Object.hasOwnProperty.call(everyDaysDetails, key)) {
            const dayArray = everyDaysDetails[key];

            // Check each object in the day's array
            for (const dayObject of dayArray) {
              if (dayObject.status) {
                const startTime = dayObject.startTime;
                const endTime = dayObject.endTime;

                if ((startTime && !endTime) || (!startTime && endTime)) {
                  this.showSnackBar("חסר נתונים ביום " + dayObject.hebrewDay, "error");
                  return;
                }
              }
            }
          }
        }

        // Update mute settings
        this.details.system_mute.mute_include_privates = this.groupValue === "כן";
        const payload = JSON.parse(JSON.stringify(this.details));
        payload.system_mute.mute_until = payload.system_mute.mute_until
          ? Util.toISOFromLocal(payload.system_mute.mute_until)
          : null;

        if (payload.system_mute.mute_until && new Date(payload.system_mute.mute_until) < new Date()) {
          this.showSnackBar("תאריך סיום ההשתקה חייב להיות בעתיד", "error");
          return;
        }

        // Save changes
        this.progressShow = true;
        const detailsJSON = JSON.stringify({ updatedObj: payload });
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/settings/save_mute_changes`;
        const res = await fetch(api, ApiServices.requestOptions("PUT", detailsJSON, token));

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בשמירת השינויים", "error");
        } else if (res.status === 200) {
          this.showSnackBar("השינויים עודכנו בהצלחה!", "success");
        }
      } catch (error) {
        this.showSnackBar("Error save changes: " + error, "error");
      } finally {
        this.progressShow = false;
      }
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color === "error" ? "red" : "green";
      this.snackbar = true;
    },
  },

  created() {
    this.getSettingsDetails();
  },
}
</script>

<style scoped>
.notification-settings-container {
  max-width: 1200px;
  margin: 0 auto;
  direction: rtl;
  font-family: 'Heebo', sans-serif;
}

.settings-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.settings-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.primary-header {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%);
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  padding: 16px;
}

.secondary-header {
  background: linear-gradient(135deg, #ffb91a 0%, #ffd166 100%);
  color: #0d2c6d;
  font-size: 1.5rem;
  font-weight: 600;
  padding: 16px;
}

.setting-section {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.setting-section.alternate {
  background-color: rgba(0, 0, 0, 0.02);
}

.setting-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.setting-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0d2c6d;
}

.compact-select {
  max-width: 100px;
}

.medium-select {
  max-width: 300px;
}

.schedule-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.table-header {
  background-color: #f5f5f5;
}

.table-header th {
  color: #0d2c6d;
  font-weight: 600;
  font-size: 1rem;
  padding: 12px;
}

.alternate-row {
  background-color: rgba(0, 0, 0, 0.02);
}

.day-name {
  font-weight: 500;
  font-size: 1rem;
}

.status-switch {
  margin: 0 auto;
}

.time-range-cell {
  padding: 8px 16px;
}

.time-range-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-input {
  max-width: 140px;
}

.action-cell {
  width: 100px;
}

.save-button {
  background: linear-gradient(0deg, rgba(255, 185, 26, 1) 0%, rgba(255, 185, 26, 0.8) 100%);
  color: #0d2c6d;
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0 32px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.save-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .notification-settings-container {
    padding: 16px;
  }

  .primary-header,
  .secondary-header {
    font-size: 1.25rem;
  }

  .setting-title {
    font-size: 1rem;
  }

  .time-range-container {
    flex-direction: column;
    align-items: stretch;
  }

  .time-range-container .mx-2 {
    margin: 8px 0;
    text-align: center;
  }

  .time-input {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .save-button {
    width: 100%;
  }

  .table-header th {
    font-size: 0.875rem;
    padding: 8px;
  }

  .day-name {
    font-size: 0.875rem;
  }
}
</style>